import { useEffect, useState } from "react"
import { formatDistanceToNow } from "date-fns"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Button } from "@/components/ui/button"
import { Loader2, RotateCcw, Trash2 } from "lucide-react"
import { toast } from "sonner"
import { useBidsStore } from "@/stores/bids-store"

interface RecentlyDeletedBidsDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
}

/**
 * Lists soft-deleted bids the caller may restore (30-day window) and restores
 * them in place. Backed by GET /bids?deleted=1 and PATCH {_restore:true}
 * (Specs/security-hardening-spec.md Part 4).
 */
export function RecentlyDeletedBidsDialog({ open, onOpenChange }: RecentlyDeletedBidsDialogProps) {
  const { deletedBids = [], fetchDeletedBids, restoreBid } = useBidsStore()

  const [isLoading, setIsLoading] = useState(false)
  const [restoringId, setRestoringId] = useState<string | null>(null)

  useEffect(() => {
    if (!open) return
    setIsLoading(true)
    void fetchDeletedBids()
      .catch(() => toast.error("Failed to load deleted bids"))
      .finally(() => setIsLoading(false))
  }, [open, fetchDeletedBids])

  const handleRestore = async (id: string, title: string) => {
    setRestoringId(id)
    try {
      await restoreBid(id)
      toast.success(`Restored ${title}`)
    } catch {
      toast.error("Failed to restore bid")
    } finally {
      setRestoringId(null)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Recently deleted</DialogTitle>
          <DialogDescription>
            Deleted bids are recoverable for 30 days, then permanently removed.
          </DialogDescription>
        </DialogHeader>

        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          </div>
        ) : deletedBids.length === 0 ? (
          <div className="flex flex-col items-center gap-2 py-12 text-center">
            <Trash2 className="h-5 w-5 text-muted-foreground" />
            <p className="text-sm text-muted-foreground">No recently deleted bids.</p>
          </div>
        ) : (
          <div className="divide-y divide-border">
            {deletedBids.map((bid) => (
              <div key={bid.id} className="flex items-center justify-between gap-4 py-3">
                <div className="min-w-0">
                  <p className="text-sm font-medium truncate">{bid.title}</p>
                  <p className="text-meta">
                    {bid.client}
                    {bid.deletedAt && (
                      <> · deleted {formatDistanceToNow(new Date(bid.deletedAt), { addSuffix: true })}</>
                    )}
                  </p>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  className="gap-1.5 shrink-0"
                  disabled={restoringId === bid.id}
                  onClick={() => void handleRestore(bid.id, bid.title)}
                >
                  {restoringId === bid.id ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <RotateCcw className="h-4 w-4" />
                  )}
                  Restore
                </Button>
              </div>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  )
}
